import { type FC, useMemo } from 'react'

import { F0Box, F0Button, F0Heading, F0Select, F0Text } from '@factorialco/f0-react'
import { CardSelectableContainer } from '@factorialco/f0-react/dist/experimental'
import { Basket, Save } from '@factorialco/f0-react/icons/app'

import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import { DEVICE_CATALOG_MAX_QUANTITY } from 'modules/it_management/hooks/device_catalog/constants'

import { MarketplaceAddonToggle } from 'modules/it_management/components/device_catalog/MarketplaceAddonToggle'
import {
  MarketplacePricingSummary,
  type RentCreditInfo,
} from 'modules/it_management/components/device_catalog/MarketplacePricingSummary'
import { NumberCounter } from 'modules/it_management/components/device_catalog/NumberCounter'
import {
  COUNTRIES,
  INSURANCE_PRICE,
  IT_SUBSCRIPTION_PRICE,
  KEYBOARD_LANGUAGES,
  SHOW_COUNTRY_SELECTOR,
  SHOW_KEYBOARD_LANGUAGE_SELECTOR,
} from 'modules/it_management/hooks/device_catalog/constants'
import type { DistributorOffer, OfferType } from 'modules/it_management/hooks/device_catalog/types'

const OFFER_TYPE_ORDER: OfferType[] = ['onetime', 'credit', 'renting']

function isOfferType(t: string | null | undefined): t is OfferType {
  return t === 'onetime' || t === 'credit' || t === 'renting'
}

function groupOffersByType(offers: DistributorOffer[]): Map<OfferType, DistributorOffer[]> {
  const map = new Map<OfferType, DistributorOffer[]>()
  for (const offer of offers) {
    const type = offer.type
    if (isOfferType(type)) {
      const list = map.get(type) ?? []
      list.push(offer)
      map.set(type, list)
    }
  }
  for (const list of map.values()) {
    list.sort((a, b) => (a.period ?? 0) - (b.period ?? 0))
  }
  return map
}

function formatPeriodDescription(offers: DistributorOffer[], type: OfferType): string {
  if (offers.length === 1) {
    const period = offers[0]?.period
    if (type === 'onetime') return i18n.t('it_management.device_catalog.detail.buy_description')
    if (type === 'renting') {
      return i18n.t('it_management.device_catalog.detail.rent_description', {
        months: period ?? 36,
      })
    }
    if (type === 'credit') {
      return i18n.t('it_management.device_catalog.detail.credit_description', {
        months: period ?? 12,
      })
    }
  }
  const periods = offers.map((o) => o.period).filter((p): p is number => p != null)
  const sorted = [...new Set(periods)].sort((a, b) => a - b)
  const min = sorted[0]
  const max = sorted[sorted.length - 1]
  const monthsLabel =
    min !== undefined && max !== undefined && min !== max ? `${min}-${max}` : (min ?? max ?? 12)
  if (type === 'renting') {
    return i18n.t('it_management.device_catalog.detail.rent_description', {
      months: monthsLabel,
    })
  }
  return i18n.t('it_management.device_catalog.detail.credit_description', {
    months: monthsLabel,
  })
}

function getTypeTitle(type: OfferType): string {
  switch (type) {
    case 'onetime':
      return i18n.t('it_management.device_catalog.detail.buy_title')
    case 'renting':
      return i18n.t('it_management.device_catalog.detail.rent_title')
    case 'credit':
      return i18n.t('it_management.device_catalog.detail.credit_title')
    default:
      return type
  }
}

export type DetailSidebarProps = {
  selectedCountry: string
  onCountryChange: (value: string) => void
  keyboardLanguage: string
  onKeyboardLanguageChange: (value: string) => void
  insuranceEnabled: boolean
  onInsuranceChange: (checked: boolean) => void
  itSubscriptionEnabled: boolean
  onItSubscriptionChange: (checked: boolean) => void
  offers: DistributorOffer[]
  selectedOfferId: string
  onSelectedOfferChange: (offerId: string) => void
  pricingLineItems: Array<{ label: string; amount: string }>
  total: { label: string; amount: string }
  rentCreditInfo?: RentCreditInfo
  quantity: number
  onQuantityChange: (quantity: number) => void
  stock?: number | null
  onAddToCart: () => void
  onSave: () => void
  isSaved: boolean
  isSavePending?: boolean
  cartItemCount: number
  onOpenCart: () => void
}

export const DetailSidebar: FC<DetailSidebarProps> = ({
  selectedCountry,
  onCountryChange,
  keyboardLanguage,
  onKeyboardLanguageChange,
  insuranceEnabled,
  onInsuranceChange,
  itSubscriptionEnabled,
  onItSubscriptionChange,
  offers,
  selectedOfferId,
  onSelectedOfferChange,
  pricingLineItems,
  total,
  rentCreditInfo,
  quantity,
  onQuantityChange,
  stock,
  onAddToCart,
  onSave,
  isSaved,
  isSavePending = false,
  cartItemCount: _cartItemCount,
  onOpenCart: _onOpenCart,
}) => {
  const offersByType = useMemo(() => groupOffersByType(offers), [offers])
  const selectedOffer = offers.find((o) => o.id === selectedOfferId)
  const selectedType =
    selectedOffer?.type && isOfferType(selectedOffer.type) ? selectedOffer.type : undefined

  const acquisitionItems = useMemo(() => {
    const items: Array<{ value: OfferType; title: string; description: string }> = []
    for (const type of OFFER_TYPE_ORDER) {
      const typeOffers = offersByType.get(type)
      if (!typeOffers || typeOffers.length === 0) continue
      items.push({
        value: type,
        title: getTypeTitle(type),
        description: formatPeriodDescription(typeOffers, type),
      })
    }
    return items
  }, [offersByType])

  const selectedTypeOffers = selectedType ? (offersByType.get(selectedType) ?? []) : []
  const hasMultipleOffersForSelectedType = selectedTypeOffers.length > 1
  const periodOptions = hasMultipleOffersForSelectedType
    ? selectedTypeOffers.map((o) => ({
        value: o.id,
        label: i18n.t('it_management.device_catalog.detail.period_months', {
          count: o.period ?? 0,
        }),
      }))
    : []

  const isMonthlyOffer =
    selectedOffer && (selectedOffer.type === 'renting' || selectedOffer.type === 'credit')
  const showRentIncludes = selectedOffer?.type === 'renting'
  const showDeviceAddOns = false
  const showSantanderPartnerBlock = false

  return (
    <div className="flex flex-col gap-4">
      <F0Box
        display="flex"
        flexDirection="column"
        gap="xl"
        border="default"
        borderColor="secondary"
        borderRadius="md"
        padding="lg"
      >
        {/* Country selector — hidden when only one country; set SHOW_COUNTRY_SELECTOR to true to show */}
        {SHOW_COUNTRY_SELECTOR && (
          <div className="flex flex-col gap-2">
            <F0Text
              variant="label"
              content={i18n.t('it_management.device_catalog.detail.country_label')}
            />
            <F0Select
              size="md"
              options={COUNTRIES}
              value={selectedCountry}
              onChange={onCountryChange}
            />
            <F0Text
              variant="description"
              content={i18n.t('it_management.device_catalog.detail.country_helper')}
            />
          </div>
        )}

        {/* Keyboard language — hidden until options come from API; set SHOW_KEYBOARD_LANGUAGE_SELECTOR to true to show */}
        {SHOW_KEYBOARD_LANGUAGE_SELECTOR && (
          <div className="flex flex-col gap-2">
            <F0Text
              variant="label"
              content={i18n.t('it_management.device_catalog.detail.keyboard_language_label')}
            />
            <F0Select
              size="md"
              options={KEYBOARD_LANGUAGES}
              value={keyboardLanguage}
              onChange={onKeyboardLanguageChange}
            />
          </div>
        )}

        {showDeviceAddOns ? (
          <>
            {/* Add-on toggles */}
            <MarketplaceAddonToggle
              title={i18n.t('it_management.device_catalog.detail.device_insurance_title')}
              priceDescription={i18n.t(
                'it_management.device_catalog.detail.device_insurance_price',
                {
                  price: formatCurrency(toCents(INSURANCE_PRICE)),
                }
              )}
              checked={insuranceEnabled}
              onCheckedChange={onInsuranceChange}
            />

            <MarketplaceAddonToggle
              title={i18n.t('it_management.device_catalog.detail.it_subscription_title')}
              priceDescription={i18n.t(
                'it_management.device_catalog.detail.it_subscription_price',
                {
                  price: formatCurrency(toCents(IT_SUBSCRIPTION_PRICE)),
                }
              )}
              checked={itSubscriptionEnabled}
              onCheckedChange={onItSubscriptionChange}
            />
          </>
        ) : null}

        {/* Acquisition model (from offers payload) */}
        <F0Box display="flex" flexDirection="column" gap="lg">
          <F0Heading
            variant="heading"
            content={i18n.t('it_management.device_catalog.detail.acquisition_model_title')}
          />
          <F0Text
            variant="description"
            content={i18n.t('it_management.device_catalog.detail.acquisition_model_description')}
          />
          {acquisitionItems.length > 0 && (
            <>
              <CardSelectableContainer
                items={acquisitionItems}
                value={selectedType ?? ''}
                onChange={(value) => {
                  if (isOfferType(value)) {
                    const typeOffers = offersByType.get(value)
                    const first = typeOffers?.[0]
                    if (first) onSelectedOfferChange(first.id)
                  }
                }}
              />
              {(hasMultipleOffersForSelectedType && periodOptions.length > 0) ||
              (isMonthlyOffer && selectedOffer) ? (
                <F0Box
                  display="flex"
                  flexDirection="column"
                  gap="md"
                  padding="lg"
                  borderRadius="md"
                  background="secondary"
                >
                  {hasMultipleOffersForSelectedType && periodOptions.length > 0 && (
                    <F0Select
                      size="md"
                      options={periodOptions}
                      value={selectedOfferId}
                      onChange={(offerId: string) => onSelectedOfferChange(offerId)}
                    />
                  )}
                  {isMonthlyOffer && selectedOffer && (
                    <>
                      <F0Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="between"
                        alignItems="center"
                      >
                        <F0Text
                          variant="description"
                          content={i18n.t('it_management.device_catalog.detail.monthly_cost')}
                        />
                        <F0Text
                          variant="body"
                          content={i18n.t(
                            'it_management.device_catalog.detail.monthly_cost_amount',
                            {
                              amount: formatCurrency(
                                toCents(selectedOffer.costWithoutVat ?? selectedOffer.cost ?? 0)
                              ),
                            }
                          )}
                        />
                      </F0Box>
                      {showRentIncludes && (
                        <F0Text
                          variant="description"
                          content={i18n.t('it_management.device_catalog.detail.rent_includes')}
                        />
                      )}
                    </>
                  )}
                </F0Box>
              ) : null}
            </>
          )}
        </F0Box>

        {/* Pricing summary (bordered) */}
        <MarketplacePricingSummary
          lineItems={pricingLineItems}
          total={total}
          vatNote={i18n.t('it_management.device_catalog.detail.vat_note')}
          rentCreditInfo={rentCreditInfo}
        />

        {/* Quantity stepper + Add to cart */}
        <F0Box display="flex" flexDirection="row" gap="md">
          <NumberCounter
            value={quantity}
            onChange={onQuantityChange}
            min={1}
            max={
              stock != null
                ? Math.min(stock, DEVICE_CATALOG_MAX_QUANTITY)
                : DEVICE_CATALOG_MAX_QUANTITY
            }
            decreaseLabel={i18n.t('it_management.device_catalog.cart.quantity_decrease')}
            increaseLabel={i18n.t('it_management.device_catalog.cart.quantity_increase')}
          />

          <F0Button
            label={i18n.t('it_management.device_catalog.detail.add_to_cart')}
            onClick={onAddToCart}
            variant="default"
            size="md"
            icon={Basket}
            fullWidth
          />
        </F0Box>

        {stock != null && (
          <F0Text
            variant="description"
            content={
              stock === 1
                ? i18n.t('it_management.device_catalog.detail.stock_available', { count: stock })
                : i18n.t('it_management.device_catalog.detail.stock_available_other', {
                    count: stock,
                  })
            }
          />
        )}

        {/* Save button (full width) */}
        <F0Button
          label={
            isSaved
              ? i18n.t('it_management.device_catalog.views.saved')
              : i18n.t('it_management.device_catalog.detail.save')
          }
          onClick={onSave}
          variant="outline"
          size="md"
          icon={Save}
          disabled={isSavePending}
          fullWidth
        />
      </F0Box>

      {showSantanderPartnerBlock ? (
        <F0Box
          display="flex"
          flexDirection="column"
          alignItems="start"
          gap="xl"
          padding="lg"
          borderRadius="md"
          background="secondary"
        >
          {/* Partner badges */}
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b8/Banco_Santander_Logotipo.svg/200px-Banco_Santander_Logotipo.svg.png"
            alt="Santander"
            className="h-5 object-contain"
          />

          {/* Partner disclaimer */}
          <F0Text
            variant="description"
            content={i18n.t('it_management.device_catalog.detail.santander_partner_disclaimer')}
          />
        </F0Box>
      ) : null}
    </div>
  )
}
